import { useMemo } from 'react';
import { sortTags, TagsAndUsersSortValues, usersColumns } from './utils';
import { useSelector } from 'react-redux';
import { TagsCenterLayout } from './TagsCenterLayout';
import { selectAccountUsers } from '../../../reducers/accountUsersReducer';
import { parseIdToValueAndNameToLabel } from '../dependencies/annotations/tags/utils';
import { UserInfo, UserStatus } from '../../../types/UserInfoTypes';
import { useTags } from '../dependencies/annotations/tags/useTags';
import useQueryParams from '../../../navigation-utils/useQueryParams';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import { useRunOnceWhenTruthy } from '../../common/useRunOnceWhenTruthy';
import { extractShortUUID } from '../../../navigation-utils/readableUUID';
import { SubPageTypes } from '../types';
import { TagRow } from './types';
import { selectCountSummaryByCrmOrgId } from '../../../reducers/documentation/userInputsReducer';
import { DocumentationTabTypes } from '../../../types/enums/DocumentationTabTypes';
import { useInitializeUserInputs } from '../dependencies/hooks/useInitializeUserInputs';
import sum from 'lodash/sum';
import { selectDependenciesHistory } from '../../../reducers/documentation/documentationReducer';
import {
  selectTopBarSearchText,
  selectTopBarSort,
} from '../../../reducers/documentation/documentationTopBarReducer';
import { SortOrder } from '../../common/types';

interface UsersCenterProps {
  onCloseDialog?: () => void;
  crmOrgId: string;
}

const useHandleDeepLinkParams = ({ crmOrgId, users }: { crmOrgId: string; users: UserInfo[] }) => {
  const { onTagClick } = useTags(crmOrgId);
  const queryParams = useQueryParams();
  const configurationItemId = queryParams.get(DeepLinkSearchParams.configurationItemId);

  useRunOnceWhenTruthy(() => {
    const res = configurationItemId?.split('|') ?? [];
    const shortTagId = extractShortUUID(res[2]);
    const user = users?.find((user) => user.id?.startsWith(shortTagId ?? ''));

    const parsedUser = user ? parseIdToValueAndNameToLabel([user])?.[0] : undefined;
    if (parsedUser) {
      onTagClick(null as any, parsedUser, true);
    }
  }, Boolean(configurationItemId));
};

export const UsersCenter = ({ onCloseDialog, crmOrgId }: UsersCenterProps) => {
  const users = useSelector(selectAccountUsers);
  const onlyActiveUsers = users?.filter((user) => user.status === UserStatus.Active);
  const parsedUsers = useMemo(
    () => parseIdToValueAndNameToLabel(onlyActiveUsers ?? []),
    [onlyActiveUsers],
  );
  const countSummary = useSelector(selectCountSummaryByCrmOrgId(crmOrgId));
  const dependenciesHistory = useSelector(selectDependenciesHistory);

  const searchValue = useSelector(selectTopBarSearchText);
  const sortState = useSelector(selectTopBarSort);

  useInitializeUserInputs(crmOrgId);

  useHandleDeepLinkParams({
    crmOrgId,
    users: onlyActiveUsers ?? [],
  });

  const filteredUsers = useMemo(() => {
    return parsedUsers?.filter((user) =>
      user.label?.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [parsedUsers, searchValue]);

  const rows: TagRow[] = useMemo(() => {
    const _filteredUsersWithCounts = (filteredUsers ?? []).map((user) => ({
      id: user.value,
      _name: user.label,
      name: user.label,
      color: user.tagColor,
      isRowActive: dependenciesHistory?.[0]?.id === user.value,
      count: sum(Object.values(countSummary?.[user.value] ?? {}).map((value) => value.length)) ?? 0,
      [DocumentationTabTypes.RECORD_TYPES]:
        countSummary?.[user.value]?.[DocumentationTabTypes.RECORD_TYPES]?.length ?? 0,
      [DocumentationTabTypes.SF_AUTOMATIONS]:
        countSummary?.[user.value]?.[DocumentationTabTypes.SF_AUTOMATIONS]?.length ?? 0,
      [DocumentationTabTypes.APEX]:
        countSummary?.[user.value]?.[DocumentationTabTypes.APEX]?.length ?? 0,
      [DocumentationTabTypes.FIELDS]:
        countSummary?.[user.value]?.[DocumentationTabTypes.FIELDS]?.length ?? 0,
      [DocumentationTabTypes.VALIDATION_RULES]:
        countSummary?.[user.value]?.[DocumentationTabTypes.VALIDATION_RULES]?.length ?? 0,
      [DocumentationTabTypes.SWEEP_AUTOMATIONS]:
        countSummary?.[user.value]?.[DocumentationTabTypes.SWEEP_AUTOMATIONS]?.length ?? 0,
      [DocumentationTabTypes.PLAYBOOK_ALERTS]:
        countSummary?.[user.value]?.[DocumentationTabTypes.PLAYBOOK_ALERTS]?.length ?? 0,
      [DocumentationTabTypes.ASSIGNMENTS]:
        countSummary?.[user.value]?.[DocumentationTabTypes.ASSIGNMENTS]?.length ?? 0,
      [DocumentationTabTypes.LAYOUTS]:
        countSummary?.[user.value]?.[DocumentationTabTypes.LAYOUTS]?.length ?? 0,
      [DocumentationTabTypes.SCHEDULED_ASSIGNMENTS]:
        countSummary?.[user.value]?.[DocumentationTabTypes.SCHEDULED_ASSIGNMENTS]?.length ?? 0,
      [DocumentationTabTypes.MATCHING_DEDUPE]:
        countSummary?.[user.value]?.[DocumentationTabTypes.MATCHING_DEDUPE]?.length ?? 0,
    }));

    return sortTags(
      _filteredUsersWithCounts,
      sortState ?? { sortBy: TagsAndUsersSortValues.name, sortOrder: SortOrder.ASC },
    );
  }, [filteredUsers, countSummary, dependenciesHistory, sortState]);

  return (
    <TagsCenterLayout
      onCloseDialog={onCloseDialog}
      columns={usersColumns}
      rows={rows}
      crmOrgId={crmOrgId}
      label="Sweep Users"
      pageType={SubPageTypes.users}
      sortProps={sortState ?? { sortBy: TagsAndUsersSortValues.name, sortOrder: SortOrder.ASC }}
    />
  );
};
